export const INTERNAL_CHAT_UNREAD_UPDATED_EVENT = 'bluezap:internal-chat-unread-updated';

export const getInternalChatUnreadMapKey = (companyIdValue, attendantIdValue) => {
  const cid = companyIdValue ?? 'none';
  const aid = attendantIdValue ?? 'none';
  return `internal_chat_unread_map_v2__${cid}__${aid}`;
};

const normalizeUnreadMap = (value) => {
  if (!value || typeof value !== 'object') return {};
  const normalized = {};
  Object.entries(value).forEach(([rawKey, rawValue]) => {
    const key = String(rawKey || '').trim();
    if (!key) return;
    const count = Number(rawValue);
    if (!Number.isFinite(count) || count <= 0) return;
    normalized[key] = Math.floor(count);
  });
  return normalized;
};

export const getInternalChatUnreadThreadIds = (map) =>
  Object.keys(normalizeUnreadMap(map));

export const getInternalChatUnreadThreadCount = (map) =>
  getInternalChatUnreadThreadIds(map).length;

export const getInternalChatUnreadTotal = (map) =>
  Object.values(normalizeUnreadMap(map)).reduce((acc, value) => acc + (Number(value) || 0), 0);

const dispatchInternalChatUnreadUpdate = (
  companyIdValue,
  attendantIdValue,
  unreadMap,
  meta = {}
) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(INTERNAL_CHAT_UNREAD_UPDATED_EVENT, {
      detail: {
        ...meta,
        companyId: companyIdValue ?? null,
        attendantId: attendantIdValue ?? null,
        unreadMap,
        totalUnreadCount: getInternalChatUnreadTotal(unreadMap),
        unreadThreadCount: getInternalChatUnreadThreadCount(unreadMap),
      },
    })
  );
};

export const writeInternalChatUnreadMap = (companyIdValue, attendantIdValue, map) => {
  if (typeof window === 'undefined') return;
  try {
    const normalized = normalizeUnreadMap(map);
    localStorage.setItem(
      getInternalChatUnreadMapKey(companyIdValue, attendantIdValue),
      JSON.stringify(normalized)
    );
  } catch {
    // ignore
  }
};

export const readInternalChatUnreadMap = (companyIdValue, attendantIdValue) => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(
      getInternalChatUnreadMapKey(companyIdValue, attendantIdValue)
    );
    if (raw) {
      const parsed = JSON.parse(raw);
      return normalizeUnreadMap(parsed);
    }
  } catch {
    // ignore
  }
  return {};
};

export const replaceInternalChatUnreadMap = (
  companyIdValue,
  attendantIdValue,
  map,
  meta = {}
) => {
  const normalized = normalizeUnreadMap(map);
  writeInternalChatUnreadMap(companyIdValue, attendantIdValue, normalized);
  dispatchInternalChatUnreadUpdate(companyIdValue, attendantIdValue, normalized, meta);
  return normalized;
};

export const incrementInternalChatUnreadThread = (
  companyIdValue,
  attendantIdValue,
  threadIdValue,
  delta = 1,
  meta = {}
) => {
  if (threadIdValue === null || threadIdValue === undefined) {
    return readInternalChatUnreadMap(companyIdValue, attendantIdValue);
  }

  const threadId = String(threadIdValue).trim();
  if (!threadId) {
    return readInternalChatUnreadMap(companyIdValue, attendantIdValue);
  }

  const current = readInternalChatUnreadMap(companyIdValue, attendantIdValue);
  const amount = Math.max(1, Math.floor(Number(delta) || 1));
  const next = {
    ...current,
    [threadId]: Math.max(1, Math.floor(Number(current[threadId] || 0)) + amount),
  };
  return replaceInternalChatUnreadMap(companyIdValue, attendantIdValue, next, meta);
};

export const clearInternalChatUnreadThreads = (
  companyIdValue,
  attendantIdValue,
  threadIds = null,
  meta = {}
) => {
  if (!Array.isArray(threadIds) || threadIds.length === 0) {
    return replaceInternalChatUnreadMap(companyIdValue, attendantIdValue, {}, meta);
  }

  const current = readInternalChatUnreadMap(companyIdValue, attendantIdValue);
  const next = { ...current };
  threadIds.forEach((threadIdValue) => {
    if (threadIdValue === null || threadIdValue === undefined) return;
    const key = String(threadIdValue).trim();
    if (!key) return;
    delete next[key];
  });
  return replaceInternalChatUnreadMap(companyIdValue, attendantIdValue, next, meta);
};
